/* ════════════════════════════════════════════════════════════════
   USA SUPPLEMENT — SHOPIFY THEME JS
   Production-ready with Shopify Theme Editor lifecycle management
   GSAP + Lenis + AJAX Cart + Variant Picker
   ════════════════════════════════════════════════════════════════ */

(function() {
  'use strict';

  /* ──────────────────────────────────────
     ACCESSIBILITY: Respect reduced motion
     ────────────────────────────────────── */
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ──────────────────────────────────────
     LENIS SMOOTH SCROLL (conditional)
     ────────────────────────────────────── */
  let lenis = null;

  function initLenis() {
    if (typeof Lenis === 'undefined' || prefersReducedMotion) return;
    try {
      lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t))
      });

      if (typeof ScrollTrigger !== 'undefined') {
        lenis.on('scroll', ScrollTrigger.update);
        gsap.ticker.add((time) => { lenis.raf(time * 1000); });
        gsap.ticker.lagSmoothing(0);
      } else {
        function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
        requestAnimationFrame(raf);
      }
    } catch (e) {
      console.warn('Lenis init failed:', e);
    }
  }

  /* ──────────────────────────────────────
     NAVBAR SCROLL EFFECT
     ────────────────────────────────────── */
  function initNavbar() {
    const nav = document.getElementById('site-nav');
    if (!nav) return;
    window.addEventListener('scroll', () => {
      nav.classList.toggle('scrolled', window.scrollY > 60);
    }, { passive: true });
  }

  /* ──────────────────────────────────────
     MOBILE BOTTOM NAV SCROLL EFFECT
     ────────────────────────────────────── */
  function initMobileBottomNav() {
    const bottomNav = document.querySelector('.mobile-bottom-nav');
    if (!bottomNav) return;
    
    let lastScrollY = window.scrollY;
    
    window.addEventListener('scroll', () => {
      const currentScrollY = window.scrollY;
      
      // Hide if scrolling down past 100px, show if scrolling up
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        bottomNav.style.transform = 'translateY(100%)';
      } else {
        bottomNav.style.transform = 'translateY(0)';
      }
      
      lastScrollY = currentScrollY;
    }, { passive: true });
    
    // Add CSS transition via JS to avoid flicker on load
    bottomNav.style.transition = 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)';
  }

  /* ──────────────────────────────────────
     GSAP SCROLL ANIMATIONS (scoped)
     Accepts optional scope element for
     Shopify Theme Editor section reload
     ────────────────────────────────────── */
  function initAnimations(scope) {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    if (prefersReducedMotion) return;

    gsap.registerPlugin(ScrollTrigger);
    const ctx = scope || document;

    /* ──────────────────────────────────────
       ELITE HORIZONTAL SCROLL SCRUB (Apple-style)
       ────────────────────────────────────── */
    function initHorizontalScrub(selector, triggerSel) {
      const track = ctx.querySelector(selector);
      const trigger = ctx.querySelector(triggerSel);
      if (!track || !trigger) return;

      function getScrollAmount() {
        let trackWidth = track.scrollWidth;
        return -(trackWidth - window.innerWidth + 100); 
      }

      if (getScrollAmount() >= 0) return; 

      gsap.to(track, {
        x: getScrollAmount,
        ease: 'none',
        scrollTrigger: {
          trigger: trigger,
          start: 'center center',
          end: () => '+=' + (track.scrollWidth - window.innerWidth),
          pin: true,
          scrub: 1, 
          invalidateOnRefresh: true,
          anticipatePin: 1
        }
      });
    }

    initHorizontalScrub('.roadmap-track', '.roadmap-section');


    // Generic reveal-up elements
    gsap.utils.toArray(
      '.reveal-up:not(.vm-card, .pillar-tile, .product-card, .cert-logo-item, .why-card, .dosage-item, .flavor-card, .stat-card, .review-card, .faq-item)',
      ctx
    ).forEach(el => {
      gsap.fromTo(el,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 90%', once: true } }
      );
    });

    // Hero entrance (only on initial load, not section reload)
    const heroContent = ctx.querySelector ? ctx.querySelector('.hero-content') : document.querySelector('.hero-content');
    if (heroContent && !scope) {
      gsap.fromTo(heroContent.children,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, stagger: 0.1, duration: 0.8, ease: 'power2.out', delay: 0.1 }
      );
      gsap.fromTo('.hero-trust-item',
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, stagger: 0.08, duration: 0.6, ease: 'power2.out', delay: 0.5 }
      );
      gsap.fromTo('.partner-logo-text',
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, stagger: 0.05, duration: 0.5, ease: 'power2.out', delay: 0.7 }
      );
      gsap.to('.hero-bg', {
        yPercent: 30, ease: 'none',
        scrollTrigger: { trigger: '.hero-section', start: 'top top', end: 'bottom top', scrub: true }
      });
    }

    // Product Cards Slide-Across Scroll Animation (Collection Page)
    gsap.utils.toArray('.collection-product-card', ctx).forEach(card => {
      let tl = gsap.timeline({
        scrollTrigger: {
          trigger: card,
          start: 'top 95%', 
          end: 'bottom 10%',
          scrub: 1.5
        }
      });
      
      tl.fromTo(card, 
        { x: -150, opacity: 0 }, 
        { x: 0, opacity: 1, duration: 1.2, ease: 'power1.out' }
      )
      .to(card, { x: 0, opacity: 1, duration: 1.5 }) // hold
      .to(card, { x: 150, opacity: 0, duration: 1.2, ease: 'power1.in' });
    });

    // Section-specific staggered animations
    const sectionAnimations = [
      { sel: '.vm-card', trigger: '.vision-section', props: { y: 30, scale: 0.98 }, to: { scale: 1 } },
      { sel: '.pillar-tile', trigger: '.pillars-grid', props: { y: 20, scale: 0.98 }, to: { scale: 1 }, stagger: 0.08, dur: 0.6 },
      { sel: '.cert-logo-item', trigger: '.cert-section', props: { y: 20 }, dur: 0.6, stagger: 0.1 },
      { sel: '.why-card', trigger: '.why-section', props: { y: 30 } },
      { sel: '.dosage-item', trigger: '.ingredient-section', props: { y: 20 }, dur: 0.6 },
      { sel: '.flavor-card', trigger: '.flavors-section', props: { scale: 0.95 }, to: { scale: 1 }, dur: 0.6 },
      { sel: '.stat-card', trigger: '.stats-grid', props: { y: 20 }, dur: 0.6 },
      { sel: '.review-card', trigger: '.reviews-section', props: { y: 30 } },
      { sel: '.roadmap-card', trigger: '.roadmap-section', props: { y: 30, filter: 'blur(5px)' }, to: { filter: 'blur(0px)' }, stagger: 0.08 },
      { sel: '.faq-item', trigger: '.faq-section', props: { y: 20 }, dur: 0.5, stagger: 0.1 }
    ];

    sectionAnimations.forEach(anim => {
      const elements = ctx.querySelectorAll ? ctx.querySelectorAll(anim.sel) : document.querySelectorAll(anim.sel);
      const triggerEl = ctx.querySelector ? ctx.querySelector(anim.trigger) : document.querySelector(anim.trigger);
      if (elements.length === 0 || !triggerEl) return;

      const fromProps = { opacity: 0, ...anim.props };
      const toProps = {
        opacity: 1, x: 0, y: 0,
        ...(anim.to || {}),
        stagger: anim.stagger || 0.15,
        duration: anim.dur || 0.8,
        ease: 'power2.out',
        scrollTrigger: { trigger: triggerEl, start: 'top 85%', once: true }
      };

      gsap.fromTo(elements, fromProps, toProps);
    });
  }

  /* ──────────────────────────────────────
     ANIMATED COUNTERS (scoped)
     ────────────────────────────────────── */
  function initCounters(scope) {
    const ctx = scope || document;
    const counters = ctx.querySelectorAll('.stat-number[data-target], .vm-stat-number[data-target]');
    if (counters.length === 0) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = parseInt(el.dataset.target);
        const suffix = el.dataset.suffix || '';
        if (isNaN(target)) { observer.unobserve(el); return; }

        let current = 0;
        el.textContent = '0' + suffix;

        const duration = 1500; // 1.5s animation duration
        const frameRate = 1000 / 60; // 60fps
        const totalFrames = Math.round(duration / frameRate);
        let frame = 0;

        const timer = setInterval(() => {
          frame++;
          const progress = frame / totalFrames;
          const easeProgress = progress * (2 - progress); // Ease out quad
          current = Math.round(easeProgress * target);

          if (frame >= totalFrames) {
            current = target;
            clearInterval(timer);
          }
          el.textContent = current + suffix;
        }, frameRate);

        observer.unobserve(el);
      });
    }, { threshold: 0.1 });

    counters.forEach(c => observer.observe(c));
  }

  /* ──────────────────────────────────────
     FAQ ACCORDION (scoped)
     ────────────────────────────────────── */
  function initFAQ(scope) {
    const ctx = scope || document;
    ctx.querySelectorAll('.faq-q').forEach(btn => {
      // Avoid duplicate listeners by checking a flag
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', () => {
        const item = btn.parentElement;
        const wasOpen = item.classList.contains('open');
        // Close all FAQ items in this section
        item.closest('.faq-list').querySelectorAll('.faq-item').forEach(i => i.classList.remove('open'));
        if (!wasOpen) item.classList.add('open');
      });
    });
  }

  /* ──────────────────────────────────────
     JOURNEY TIMELINE (VERTICAL)
     ────────────────────────────────────── */
  function initJourneyTimeline(scope) {
    const ctx = scope || document;
    const timeline = ctx.querySelector('.journey-timeline');
    if (!timeline) return;

    // Filter Logic
    const navBtns = ctx.querySelectorAll('.roadmap-nav-btn');
    const rows = ctx.querySelectorAll('.journey-node-row');

    if (navBtns.length > 0 && rows.length > 0) {
      navBtns.forEach(btn => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', () => {
          navBtns.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          const selectedYear = btn.dataset.year;

          rows.forEach(row => {
            const rowYear = row.dataset.year;
            const show = (selectedYear === 'all' || rowYear === selectedYear);
            if (show) {
              row.classList.remove('hidden');
            } else {
              row.classList.add('hidden');
            }
          });
          ScrollTrigger.refresh(); // Refresh GSAP triggers after layout change
        });
      });
    }

    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    gsap.registerPlugin(ScrollTrigger);

    // Animate the central progress line
    const progressLine = ctx.querySelector('#journey-progress-line');
    if (progressLine) {
      gsap.to(progressLine, {
        height: '100%',
        ease: 'none',
        scrollTrigger: {
          trigger: timeline,
          start: 'top 50%',
          end: 'bottom 50%',
          scrub: 1
        }
      });
    }

    // Animate each card and dot
    const cards = ctx.querySelectorAll('.journey-card');
    cards.forEach(card => {
      const row = card.closest('.journey-node-row');
      const dot = row.querySelector('.journey-dot');
      const isLeft = row.classList.contains('journey-left');

      let tl = gsap.timeline({
        scrollTrigger: {
          trigger: row,
          start: 'top 70%', // Trigger when the row enters 70% from top
          end: 'bottom 50%',
          toggleActions: 'play none none reverse' // Play forward when entering, reverse when scrolling up
        }
      });

      // Activate the dot
      tl.call(() => dot.classList.add('active'))
        .fromTo(card,
          { x: isLeft ? -50 : 50, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.6, ease: 'power2.out' },
          '<'
        );
      
      // Remove active class when scrolling up
      ScrollTrigger.create({
        trigger: row,
        start: 'top 70%',
        onLeaveBack: () => dot.classList.remove('active')
      });
    });
  }

  /* ──────────────────────────────────────
     PRODUCT GALLERY — Auto-slider (scoped)
     ────────────────────────────────────── */
  function initProductGallery(scope) {
    const ctx = scope || document;
    const thumbnailsContainer = ctx.querySelector('.pd-thumbnails');
    if (!thumbnailsContainer) return;

    const thumbs = Array.from(thumbnailsContainer.querySelectorAll('.pd-thumb'));
    if (thumbs.length <= 1) return;

    let autoSlideInterval;

    const startAutoSlide = () => {
      autoSlideInterval = setInterval(() => {
        let currentIndex = thumbs.findIndex(t => t.classList.contains('active'));
        if (currentIndex === -1) currentIndex = 0;
        let nextIndex = (currentIndex + 1) % thumbs.length;
        thumbs[nextIndex].click();
      }, 4000);
    };

    startAutoSlide();

    const galleryCol = ctx.querySelector('.pd-gallery-col');
    if (galleryCol) {
      galleryCol.addEventListener('mouseenter', () => clearInterval(autoSlideInterval));
      galleryCol.addEventListener('mouseleave', startAutoSlide);
    }

    // Store interval reference for cleanup on section unload
    if (scope) scope.__autoSlideInterval = autoSlideInterval;
  }

  /* ──────────────────────────────────────
     PRODUCT VARIANT PICKER
     Wires flavor/option buttons to
     Shopify variant IDs and AJAX Cart
     ────────────────────────────────────── */
  function initVariantPicker(scope) {
    const ctx = scope || document;
    const flavorBtns = ctx.querySelectorAll('.pd-flavor-btn');
    if (flavorBtns.length === 0) return;

    flavorBtns.forEach(btn => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', (e) => {
        const grid = e.target.closest('.pd-flavor-grid');
        grid.querySelectorAll('.pd-flavor-btn').forEach(b => b.classList.remove('active'));
        e.target.classList.add('active');

        // Build selected options and find matching Shopify variant
        updateVariantFromOptions(ctx);
      });
    });
  }

  function updateVariantFromOptions(ctx) {
    const form = ctx.querySelector('.pd-form');
    if (!form) return;

    // Collect all selected option values
    const selectedOptions = [];
    ctx.querySelectorAll('.pd-option-group').forEach(group => {
      const activeBtn = group.querySelector('.pd-flavor-btn.active');
      if (activeBtn) selectedOptions.push(activeBtn.dataset.optionValue);
    });

    // Find matching variant from the product JSON (if available)
    const productJSON = ctx.querySelector('[data-product-json]');
    if (productJSON) {
      try {
        const product = JSON.parse(productJSON.textContent);
        const matchingVariant = product.variants.find(v => {
          return selectedOptions.every((opt, i) => v.options[i] === opt);
        });
        if (matchingVariant) {
          form.querySelector('input[name="id"]').value = matchingVariant.id;
          
          // Update base price data-attribute
          const priceMainEl = ctx.querySelector('#pd-price-main-el');
          if (priceMainEl) {
            priceMainEl.dataset.basePrice = matchingVariant.price;
          }

          // Update One-time card price text
          const onetimePriceEl = ctx.querySelector('.pd-card-price-onetime');
          if (onetimePriceEl) {
            onetimePriceEl.textContent = formatMoney(matchingVariant.price);
          }

          // Recalculate price based on active card (One-time or Subscribe)
          const activeCard = ctx.querySelector('.pd-card-opt.active');
          if (activeCard) {
            updatePriceDisplayFromCard(ctx, activeCard);
          } else {
            const priceEl = ctx.querySelector('.pd-price');
            if (priceEl) priceEl.textContent = formatMoney(matchingVariant.price);
          }

          // Update compare-at price
          const compareEl = ctx.querySelector('.pd-compare-price');
          if (compareEl) {
            if (matchingVariant.compare_at_price > matchingVariant.price) {
              compareEl.innerHTML = '<s>' + formatMoney(matchingVariant.compare_at_price) + '</s>';
              compareEl.style.display = '';
            } else {
              compareEl.style.display = 'none';
            }
          }

          // Update add-to-cart button state
          const addBtn = ctx.querySelector('.pd-btn-black') || ctx.querySelector('.pd-add-btn');
          const buyNowBtn = ctx.querySelector('#pd-buy-now-btn');
          if (addBtn) {
            if (matchingVariant.available) {
              addBtn.disabled = false;
              if (addBtn.classList.contains('pd-add-btn')) {
                addBtn.textContent = 'Add to Cart — ' + formatMoney(matchingVariant.price);
              } else {
                addBtn.textContent = 'Add to Cart';
              }
            } else {
              addBtn.disabled = true;
              addBtn.textContent = 'Sold Out';
            }
          }
          if (buyNowBtn) {
            buyNowBtn.disabled = !matchingVariant.available;
          }
        }
      } catch (e) {
        console.warn('Variant update failed:', e);
      }
    }
  }

  function updatePriceDisplayFromCard(ctx, card) {
    const priceMainEl = ctx.querySelector('#pd-price-main-el');
    const sellingPlanInput = ctx.querySelector('#pd-selling-plan-input');
    if (!priceMainEl) return;

    const basePrice = parseInt(priceMainEl.dataset.basePrice) || 0;
    const purchaseType = card.dataset.purchaseType;

    if (purchaseType === 'subscribe') {
      const planId = card.dataset.sellingPlanId || '';
      if (sellingPlanInput) sellingPlanInput.value = planId;
      
      // Calculate 15% discount
      const discountedPrice = Math.round(basePrice * 0.85);
      priceMainEl.textContent = formatMoney(discountedPrice);
    } else {
      if (sellingPlanInput) sellingPlanInput.value = '';
      priceMainEl.textContent = formatMoney(basePrice);
    }
  }

  async function handleBuyNow(ctx, btn) {
    const form = btn.closest('.pd-form');
    if (!form) return;

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Processing...';

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        window.location.href = '/checkout';
      } else {
        const error = await response.json();
        btn.textContent = error.description || 'Error — Try Again';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      }
    } catch (err) {
      console.error('Buy Now error:', err);
      window.location.href = '/checkout';
    }
  }

  function initPurchaseControls(scope) {
    const ctx = scope || document;
    
    // Purchase Card Options (One-time vs Subscription)
    const cards = ctx.querySelectorAll('.pd-card-opt:not(.disabled-plan)');
    cards.forEach(card => {
      if (card.dataset.bound) return;
      card.dataset.bound = 'true';
      card.addEventListener('click', () => {
        const container = card.closest('.pd-purchase-cards');
        if (container) {
          container.querySelectorAll('.pd-card-opt').forEach(c => c.classList.remove('active'));
        }
        card.classList.add('active');
        updatePriceDisplayFromCard(ctx, card);
      });
    });

    // Quantity selectors inside actions row
    const qtyBoxes = ctx.querySelectorAll('.pd-qty-box');
    qtyBoxes.forEach(box => {
      const input = box.querySelector('input');
      const decBtn = box.querySelector('[data-action="decrease"]');
      const incBtn = box.querySelector('[data-action="increase"]');
      if (!input || !decBtn || !incBtn) return;

      if (!decBtn.dataset.bound) {
        decBtn.dataset.bound = 'true';
        decBtn.addEventListener('click', () => {
          let val = parseInt(input.value) || 1;
          if (val > 1) input.value = val - 1;
        });
      }

      if (!incBtn.dataset.bound) {
        incBtn.dataset.bound = 'true';
        incBtn.addEventListener('click', () => {
          let val = parseInt(input.value) || 1;
          if (val < 99) input.value = val + 1;
        });
      }
    });

    // Buy Now click trigger
    const buyNowBtn = ctx.querySelector('#pd-buy-now-btn');
    if (buyNowBtn && !buyNowBtn.dataset.bound) {
      buyNowBtn.dataset.bound = 'true';
      buyNowBtn.addEventListener('click', () => {
        handleBuyNow(ctx, buyNowBtn);
      });
    }
  }

  function formatMoney(cents) {
    return 'Rs. ' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* ──────────────────────────────────────
     QUANTITY SELECTOR (scoped)
     ────────────────────────────────────── */
  function initQuantitySelector(scope) {
    const ctx = scope || document;
    ctx.querySelectorAll('.pd-qty').forEach(container => {
      const btns = container.querySelectorAll('.pd-qty-btn');
      const input = container.querySelector('.pd-qty-input');
      if (!input || btns.length < 2) return;

      btns.forEach(btn => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', () => {
          let val = parseInt(input.value) || 1;
          if (btn.dataset.action === 'decrease' && val > 1) input.value = val - 1;
          if (btn.dataset.action === 'increase' && val < 99) input.value = val + 1;
        });
      });
    });
  }

  /* ──────────────────────────────────────
     AJAX CART — Add to Cart via Fetch API
     Uses Shopify's /cart/add.js endpoint
     ────────────────────────────────────── */
  function initAJAXCart(scope) {
    const ctx = scope || document;
    const forms = ctx.querySelectorAll('.pd-form');

    forms.forEach(form => {
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const submitBtn = form.querySelector('.pd-btn-black') || form.querySelector('.pd-add-btn') || form.querySelector('button[type="submit"]');
        if (!submitBtn || submitBtn.disabled) return;

        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Adding...';
        submitBtn.disabled = true;

        try {
          const formData = new FormData(form);
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            submitBtn.textContent = '✓ Added to Cart!';
            submitBtn.style.background = 'var(--usa-green)';

            // Update cart count in header
            updateCartCount();

            setTimeout(() => {
              submitBtn.textContent = originalText;
              submitBtn.style.background = '';
              submitBtn.disabled = false;
            }, 2000);
          } else {
            const error = await response.json();
            submitBtn.textContent = error.description || 'Error — Try Again';
            submitBtn.style.background = 'var(--usa-red)';
            setTimeout(() => {
              submitBtn.textContent = originalText;
              submitBtn.style.background = '';
              submitBtn.disabled = false;
            }, 2500);
          }
        } catch (err) {
          console.error('Cart add error:', err);
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }
      });
    });
  }

  async function updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('.cart-count').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });
    } catch (e) { /* silent fail */ }
  }

  /* ──────────────────────────────────────
     NEWSLETTER FORM
     ────────────────────────────────────── */
  function initNewsletter(scope) {
    const ctx = scope || document;
    const form = ctx.querySelector('#newsletter-form');
    if (!form || form.dataset.bound) return;
    form.dataset.bound = 'true';

    form.addEventListener('submit', (e) => {
      // On Shopify, the native form will POST to /contact
      // This provides visual feedback before redirect
      const input = form.querySelector('input[type="email"]');
      if (input && input.value) {
        // Let the form submit naturally to Shopify
        // but show a quick visual cue
        const btn = form.querySelector('button[type="submit"]');
        if (btn) {
          btn.textContent = '✓ Subscribed!';
          btn.style.background = 'var(--usa-green)';
        }
      }
    });
  }

  /* ──────────────────────────────────────
     MICRO-INTERACTIONS (global, once)
     Magnetic buttons + 3D tilt + spotlight
     ────────────────────────────────────── */
  function initMicroInteractions() {
    if (prefersReducedMotion) return;

    // Magnetic Buttons
    document.querySelectorAll('.btn').forEach(btn => {
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        btn.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = `translate(0px, 0px)`;
      });
    });

    // 3D Tilt on Product Cards
    document.querySelectorAll('.product-card, .collection-product-card').forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const rotateX = ((y - rect.height / 2) / (rect.height / 2)) * -4;
        const rotateY = ((x - rect.width / 2) / (rect.width / 2)) * 4;
        card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-5px)`;
      });
      card.addEventListener('mouseleave', () => {
        card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)`;
      });
    });

    // Cursor spotlight tracking
    document.addEventListener('mousemove', (e) => {
      const selector = '.spotlight-card, .vm-card, .pillar-tile, .product-card, .collection-product-card, .why-card, .stat-card, .roadmap-card';
      document.querySelectorAll(selector).forEach(card => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
        card.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
      });
    }, { passive: true });

    // Smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ════════════════════════════════════════
     GLOBAL IMAGE HELPER
     Called by onclick in product thumbnails
     ════════════════════════════════════════ */
  window.changeMainImage = function(thumbEl, fullSrc) {
    const mainImg = document.getElementById('pdMainImage');
    if (mainImg) {
      mainImg.style.opacity = '0';
      mainImg.style.transform = 'translateX(-15px)';
      
      setTimeout(() => {
        mainImg.src = fullSrc;
        mainImg.style.transform = 'translateX(15px)';
        
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            mainImg.style.opacity = '1';
            mainImg.style.transform = 'translateX(0)';
          });
        });
      }, 250);
    }
    // Update active state
    const container = thumbEl.closest('.pd-thumbnails');
    if (container) {
      container.querySelectorAll('.pd-thumb').forEach(t => t.classList.remove('active'));
      thumbEl.classList.add('active');
    }
  };

  /* ════════════════════════════════════════
     MOBILE NAVIGATION DRAWER
     ════════════════════════════════════════ */
  function initMobileDrawer(scope) {
    const ctx = scope || document;
    const toggle = ctx.querySelector('.mobile-menu-toggle');
    const close = ctx.querySelector('.mobile-drawer-close');
    const overlay = ctx.querySelector('.mobile-drawer-overlay');
    const drawer = ctx.querySelector('.mobile-drawer');
    const links = ctx.querySelectorAll('.mobile-nav-links a');

    if (!toggle || !drawer) return;

    const openDrawer = () => {
      drawer.classList.add('open');
      document.body.classList.add('drawer-open');
    };

    const closeDrawer = () => {
      drawer.classList.remove('open');
      document.body.classList.remove('drawer-open');
    };

    if (!toggle.dataset.bound) {
      toggle.dataset.bound = 'true';
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        if (drawer.classList.contains('open')) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });
    }

    if (close && !close.dataset.bound) {
      close.dataset.bound = 'true';
      close.addEventListener('click', (e) => {
        e.preventDefault();
        closeDrawer();
      });
    }

    if (overlay && !overlay.dataset.bound) {
      overlay.dataset.bound = 'true';
      overlay.addEventListener('click', closeDrawer);
    }

    links.forEach(link => {
      if (!link.dataset.bound) {
        link.dataset.bound = 'true';
        link.addEventListener('click', closeDrawer);
      }
    });
  }

  // ═══ AUTO-SLIDING PRODUCTS CAROUSEL (MOBILE) ═══
  function initProductAutoplay(scope) {
    const ctx = scope || document;
    const grid = ctx.querySelector ? ctx.querySelector('.products-grid') : document.querySelector('.products-grid');
    if (!grid) return;

    let scrollInterval;

    const startAutoplay = () => {
      clearInterval(scrollInterval);
      scrollInterval = setInterval(() => {
        if (window.innerWidth > 992) return;
        
        const card = grid.querySelector('.product-card');
        if (!card) return;
        const cardWidth = card.offsetWidth;
        const gap = 16; // gap between cards on mobile
        const step = cardWidth + gap;
        
        const maxScroll = grid.scrollWidth - grid.clientWidth;
        if (grid.scrollLeft >= maxScroll - 5) {
          grid.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          grid.scrollBy({ left: step, behavior: 'smooth' });
        }
      }, 3000);
    };

    startAutoplay();

    // Stop autoplay on user interaction
    const stopAutoplay = () => clearInterval(scrollInterval);
    grid.addEventListener('touchstart', stopAutoplay, { passive: true });
    grid.addEventListener('mousedown', stopAutoplay, { passive: true });

    // Resume autoplay after user interaction stops
    let resumeTimeout;
    const interactionEnd = () => {
      clearTimeout(resumeTimeout);
      resumeTimeout = setTimeout(startAutoplay, 5000);
    };
    grid.addEventListener('touchend', interactionEnd, { passive: true });
    grid.addEventListener('mouseup', interactionEnd, { passive: true });

    // Store interval on the grid element so we can clean it up on unload
    grid.__productAutoplayInterval = scrollInterval;
  }

  /* ════════════════════════════════════════
     MASTER INIT — Called on DOMContentLoaded
     ════════════════════════════════════════ */
  function initAll(scope) {
    initMobileDrawer(scope);
    initCounters(scope);
    initFAQ(scope);
    initJourneyTimeline(scope);
    initProductGallery(scope);
    initVariantPicker(scope);
    initQuantitySelector(scope);
    initPurchaseControls(scope);
    initAJAXCart(scope);
    initNewsletter(scope);
    initProductAutoplay(scope);
  }

  /* ════════════════════════════════════════
     EVENT LISTENERS
     ════════════════════════════════════════ */

  // Initial page load
  document.addEventListener('DOMContentLoaded', () => {
    initNavbar();
    initMobileBottomNav();
    initLenis();
    initAll();
    initMicroInteractions();

    // Delay GSAP slightly to ensure libraries are loaded
    if (typeof gsap !== 'undefined') {
      initAnimations();
    } else {
      const waitForGSAP = setInterval(() => {
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
          clearInterval(waitForGSAP);
          initAnimations();
        }
      }, 150);
      // Safety timeout — stop waiting after 5 seconds
      setTimeout(() => clearInterval(waitForGSAP), 5000);
    }
  });

  /* ════════════════════════════════════════
     SHOPIFY THEME EDITOR LIFECYCLE HOOKS
     
     These events fire when a merchant:
     - Adds/removes/reorders sections in Customize
     - Changes section settings
     - Clicks into a section to edit it
     
     Without these, GSAP animations break,
     event listeners leak, and intervals orphan.
     ════════════════════════════════════════ */

  // A section was added or reloaded in the Theme Editor
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target;
    initAll(section);
    initAnimations(section);
  });

  // A section was removed — clean up to prevent memory leaks
  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target;

    // Kill any auto-slide intervals attached to this section
    if (section.__autoSlideInterval) {
      clearInterval(section.__autoSlideInterval);
    }
    const grid = section.querySelector('.products-grid');
    if (grid && grid.__productAutoplayInterval) {
      clearInterval(grid.__productAutoplayInterval);
    }

    // Kill orphaned GSAP ScrollTrigger instances scoped to this section
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.getAll().forEach(st => {
        if (section.contains(st.trigger)) st.kill();
      });
    }
  });

  // A section was selected (clicked) in the Theme Editor
  document.addEventListener('shopify:section:select', (e) => {
    // Scroll the section into view smoothly
    e.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  // A block was selected in the Theme Editor (e.g., a specific FAQ item)
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;

    // If it's a FAQ item, open it
    if (block.classList.contains('faq-item')) {
      block.classList.add('open');
    }

    // Scroll block into view
    block.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  // A block was deselected
  document.addEventListener('shopify:block:deselect', (e) => {
    const block = e.target;
    if (block.classList.contains('faq-item')) {
      block.classList.remove('open');
    }
  });

})();
